/**
 * v-latex 指令（移动端，保守渲染策略）
 *
 * 用法与 v-html 一致：先把绑定值作为 HTML 渲染到元素内部。
 * 只有内容明显是「简单文本 + LaTeX 公式分隔符」时，才会调用 KaTeX
 * auto-render；只要看起来像复杂富文本（Word / TinyMCE 粘贴产生的
 * table / inline style / 已有公式产物等），就完全跳过 KaTeX，
 * 让原始 HTML 自己排版，避免破坏题库历史数据的展示。
 *
 * 性能策略：
 * 1. 字符串相同则跳过，避免父组件重渲染时的无谓重排。
 * 2. 不含公式分隔符的内容直接跳过 KaTeX。
 * 3. 渲染请求统一进入全局队列，按帧批量小批次处理。
 * 4. IntersectionObserver 懒渲染：元素接近视口时才入队。
 *
 * 注意：KaTeX CSS 已在 src/scss/index.scss 通过 `@import '~katex/dist/katex.min.css'`
 * 打入 bundle，这里不再重复 import。
 */

import Vue, { DirectiveOptions } from 'vue'
// @ts-ignore - 子路径无 TS 类型；webpack 4 不识别 katex package.json 的 exports 字段，必须用直接路径
import renderMathInElement from 'katex/dist/contrib/auto-render'
// 把 Shell/普通 $ 转义掉，避免 echo $PATH、$(date) 等被 KaTeX 误吞为公式
// 这里走 'html' 模式：把"非公式"的 $ 包成 <span class="vlsd">$</span>，
// 利用 KaTeX renderElem 不跨 element 合并 text 节点的特性来阻止配对。
import { escapeNonMathDollars, SKIP_DOLLAR_CLASS } from '@/utils/latex/escapeShellDollars'

interface RenderMathDelimiter {
  left: string
  right: string
  display: boolean
}

interface RenderMathOptions {
  delimiters?: RenderMathDelimiter[]
  ignoredTags?: string[]
  ignoredClasses?: string[]
  throwOnError?: boolean
  errorCallback?: (msg: string, err: Error) => void
}

const DEFAULT_OPTIONS: RenderMathOptions = {
  delimiters: [
    { left: '$$', right: '$$', display: true },
    { left: '\\[', right: '\\]', display: true },
    { left: '\\(', right: '\\)', display: false },
    { left: '$', right: '$', display: false }
  ],
  // 这些标签内的内容多半是已排版好的富文本/已渲染的公式，二次扫描容易破坏布局。
  ignoredTags: ['script', 'noscript', 'style', 'textarea', 'pre', 'code', 'table'],
  // SKIP_DOLLAR_CLASS（'vlsd'）是 escapeNonMathDollars('html') 包裹"非公式 $"用的兜底类，
  // 加在这里作为防御性双保险——即使 splitAtDelimiters 越过 element 边界，KaTeX 也会跳过。
  ignoredClasses: ['katex', 'katex-display', 'MathJax', 'MathJax_Display', 'mq-math-mode', SKIP_DOLLAR_CLASS],
  throwOnError: false,
  errorCallback() { /* 静默：避免脏数据刷屏 */ }
}

const MAX_PER_FRAME = 4

const RENDERED_CLASS = 'v-latex-rendered'
const SKIPPED_CLASS = 'v-latex-skipped'

const FORMULA_DELIMITER_RE = /\$\$|\$|\\\(|\\\[/

// 命中以下任一特征就认为是「复杂富文本」，不再交给 KaTeX 二次渲染。
// 之所以保守：题库历史数据多来自 Word/TinyMCE，粘贴后的 HTML 内嵌大量
// inline 样式 / 表格 / 已经渲染好的公式产物（kity、wiris、mathjax、katex），
// 二次 auto-render 会把它们的 DOM 重写一遍，导致排版被打散。
const COMPLEX_RICHTEXT_RE = /<\s*(table|tbody|thead|tr|td|th|svg|math|img|iframe)\b|class\s*=\s*["'][^"']*\b(katex|MathJax|mjx-|mq-math-mode|kity|wiris|Wirisformula)\b|data-(latex|mathml|wiris|formula)\s*=|<[^>]+\sstyle\s*=\s*["'][^"']*(mso-|font-family\s*:|position\s*:\s*absolute)/i

function mayContainFormula(html: string): boolean {
  if (!html) return false
  return FORMULA_DELIMITER_RE.test(html)
}

function isComplexRichText(html: string): boolean {
  if (!html) return false
  return COMPLEX_RICHTEXT_RE.test(html)
}

interface LatexElement extends HTMLElement {
  __vLatexLast__?: string
  __vLatexPending__?: boolean
}

const renderQueue: LatexElement[] = []
let flushScheduled = false

function scheduleFlush() {
  if (flushScheduled) return
  flushScheduled = true

  const tick = () => {
    flushScheduled = false
    let processed = 0
    while (processed < MAX_PER_FRAME && renderQueue.length > 0) {
      const el = renderQueue.shift() as LatexElement
      if (!el || !el.__vLatexPending__) continue
      el.__vLatexPending__ = false
      if (!el.isConnected) continue
      try {
        renderMathInElement(el, DEFAULT_OPTIONS as any)
      } catch { /* 静默 */ }
      processed++
    }
    if (renderQueue.length > 0) scheduleFlush()
  }

  const w = window as any
  if (typeof w.requestIdleCallback === 'function') {
    w.requestIdleCallback(tick, { timeout: 200 })
  } else {
    requestAnimationFrame(tick)
  }
}

function enqueue(el: LatexElement) {
  if (el.__vLatexPending__) return
  el.__vLatexPending__ = true
  renderQueue.push(el)
  scheduleFlush()
}

const observer: IntersectionObserver | null =
  typeof window !== 'undefined' && typeof window.IntersectionObserver !== 'undefined'
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              const el = entry.target as LatexElement
              observer!.unobserve(el)
              enqueue(el)
            }
          }
        },
        { rootMargin: '200px 0px' }
      )
    : null

function requestRender(el: LatexElement) {
  if (observer) observer.observe(el)
  else enqueue(el)
}

function renderLatex(el: LatexElement, value: any) {
  // 先做一次 Shell/普通 $ 转义，再走原有渲染流程；纯文本和不含 $ 的输入是 O(1)。
  // mode='html'：用 <span class="vlsd">$</span> 包"非公式 $"，避免 \$ 在 innerHTML
  // 里以字面反斜杠的形态显示出来。
  const html = escapeNonMathDollars(value == null ? '' : String(value), 'html')
  if (el.__vLatexLast__ === html) return
  el.__vLatexLast__ = html

  el.classList.add(RENDERED_CLASS)
  el.innerHTML = html

  if (!mayContainFormula(html) || isComplexRichText(html)) {
    el.classList.add(SKIPPED_CLASS)
    if (observer) observer.unobserve(el)
    return
  }

  el.classList.remove(SKIPPED_CLASS)
  requestRender(el)
}

const latexDirective: DirectiveOptions = {
  bind(el, binding) {
    renderLatex(el as LatexElement, binding.value)
  },
  update(el, binding) {
    renderLatex(el as LatexElement, binding.value)
  },
  unbind(el) {
    const target = el as LatexElement
    target.__vLatexPending__ = false
    if (observer) observer.unobserve(target)
  }
}

Vue.directive('latex', latexDirective)

export default latexDirective
